<script setup lang="ts">
import { computed, ref, unref, watch, type ComputedRef } from 'vue';
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useForm } from 'vee-validate';
import * as yup from 'yup';

import { useBlocksStore, useConfigurationStore } from '@/stores';
import type { ProjectModuleDto, ProjectConsultationListItemDto } from '@/services/types/api.types';
import type { SavingOptions } from '@/services/types/file-uploader.types';
import type { FileInfo } from '@/services/types/file.types';
import { useLanguageValidationErrors } from '@/composables';
import { useWatchItemLoading } from '@/composables/watch-item-loading';
import { usePageBlockImage } from '@/composables/usePageBlockImage';
import { blockTypeName, editableBlockConfig } from '@/helpers/block.helper';
import { wrapSavingOperation } from '@/helpers/notifications.helper';
import { provideEditLocale, getModuleDisplayTitleWithFallback } from '@/composables/data-display-utils';
import { useBlockParent } from '@/composables/useBlockParent';
import { formSaveImageGuard, formSaveNavigationGuard, hasRouteChanged, resetFormToItem } from '@/helpers/form.helper';
import { localizableString } from '@/helpers/validation.helper';
import { Constants } from '@/constants';
import { useModuleBreadcrumbOptions } from '@/composables/breadcrumb-options';
import { useRouteNumberParam } from '@/composables/use-route-param';
import type { BreadcrumbElement } from '@/helpers/breadcrumbs.helper';
import { ModuleTypeEnum, type BlockParent, type BlockParentType, type BlockRouteFor } from '@/types/modules';

import FormActions from '@/components/FormActions.vue';
import DynamicTabs from '@/components/DynamicTabs.vue';
import ModuleTextImage from '@/components/module/ModuleTextImage.vue';
import ModuleVideo from '@/components/module/ModuleVideo.vue';
import ModuleConsultation from '@/components/module/ModuleConsultation.vue';
import ModuleAttachments from '@/components/module/ModuleAttachments.vue';
import ModuleFaq from '@/components/module/ModuleFaq.vue';
import ContextList from '@/components/ContextList.vue';
import FaqList from '@/components/FaqList.vue';
import ModuleSubscription from '@/components/module/ModuleSubscription.vue';
import ModuleTimeline from '@/components/module/ModuleTimeline.vue';
import ModuleHero from '@/components/module/ModuleHero.vue';
import ModuleFeedback from '@/components/module/ModuleFeedback.vue';
import ModuleQuestionAnswer from '@/components/module/ModuleQuestionAnswer.vue';
import TimelineStepList from '@/components/TimelineStepList.vue';
import ModuleContactList from '@/components/module/ModuleContactList.vue';
import ContactListMemberList from '@/components/ContactListMemberList.vue';
import FeedbackEntryList from '@/components/FeedbackEntryList.vue';
import QuestionAnswerList from '@/components/QuestionAnswerList.vue';
import DocumentSubmissionList from '@/components/DocumentSubmissionList.vue';
import TopPageNavigation from '@/components/TopPageNavigation.vue';
import TabCtaButtons from '@/components/module/TabCtaButtons.vue';
import ModuleEvent from '@/components/module/ModuleEvent.vue';
import EventList from '@/components/EventList.vue';
import ModuleNews from '@/components/module/ModuleNews.vue';
import ModuleMap from '@/components/module/ModuleMap.vue';
import ModuleDocumentSubmission from '@/components/module/ModuleDocumentSubmission.vue';
import NewsList from '@/components/NewsList.vue';
import { cloneDeep } from 'lodash';

// PROPS
// Each block route states its parent: its kind, the route and icon of the screen that lists its blocks.
const props = defineProps<{ parentType: BlockParentType; parentRouteName: string; parentIcon: string }>();

const { t } = useI18n();

// STORE
const blocksStore = useBlocksStore();
const configurationStore = useConfigurationStore();

// CONST
const moduleTypes = blocksStore.getTypes();
const route = useRoute();
const moduleId = useRouteNumberParam('moduleId');
// The parent's id, under the name each kind of parent's block routes give it.
const parentIds: Record<BlockParentType, ComputedRef<number>> = {
  [Constants.Block.ParentType.Project]: useRouteNumberParam('projectId'),
  [Constants.Block.ParentType.Page]: useRouteNumberParam('pageId'),
};
const parentId = computed(() => parentIds[props.parentType].value);
const blockParent = computed<BlockParent>(() => ({ type: props.parentType, id: parentId.value }));
const parentLocation = computed(() => ({ name: props.parentRouteName, params: { id: parentId.value } }));
// A sibling block opens on this block's own route, under the same parent.
const blockRouteFor: BlockRouteFor = (blockId) => ({
  name: route.name,
  params: { ...route.params, moduleId: blockId },
});

const componentRegistry: Record<number, any> = {
  [ModuleTypeEnum.Text]: ModuleTextImage,
  [ModuleTypeEnum.Video]: ModuleVideo,
  [ModuleTypeEnum.Consultation]: ModuleConsultation,
  [ModuleTypeEnum.Attachments]: ModuleAttachments,
  [ModuleTypeEnum.Faq]: ModuleFaq,
  [ModuleTypeEnum.Subscription]: ModuleSubscription,
  [ModuleTypeEnum.Timeline]: ModuleTimeline,
  [ModuleTypeEnum.ContactList]: ModuleContactList,
  [ModuleTypeEnum.Hero]: ModuleHero,
  [ModuleTypeEnum.Event]: ModuleEvent,
  [ModuleTypeEnum.Feedback]: ModuleFeedback,
  [ModuleTypeEnum.QuestionAnswer]: ModuleQuestionAnswer,
  [ModuleTypeEnum.News]: ModuleNews,
  [ModuleTypeEnum.Map]: ModuleMap,
  [ModuleTypeEnum.DocumentSubmission]: ModuleDocumentSubmission,
};

// A project block's image has its own route, which stores it at once and refetches the block. The refetch would
// discard unsaved edits, so a clear asks first.
const saveProjectBlockImage = async (options: SavingOptions) => {
  const block = module.value;
  if (!block) return;
  wrapSavingOperation(async () => await blocksStore.saveModuleImage(block, options));
};

const removeProjectBanner = async () => {
  if (!module.value) return;
  await blocksStore.removeModuleImage(module.value);
};

const clearProjectBlockImage = async () => {
  formSaveImageGuard(isDirty.value, removeProjectBanner);
};

// A page block has no image route: its image is a form field like the others.
const pageBlockImage = usePageBlockImage(
  () => values.config?.image as FileInfo | null | undefined,
  (image) => setFieldValue('config.image', image),
);

const imageHandlers = computed(() =>
  props.parentType === Constants.Block.ParentType.Page
    ? pageBlockImage
    : { save: saveProjectBlockImage, clear: clearProjectBlockImage },
);

// REF
const openedTab = ref<string>('infos');
const dynamicTabsRef = ref<InstanceType<typeof DynamicTabs> | null>(null);
const { project, title: parentTitle, languageEnabled, themeHost, themeKey, blockLabels } = useBlockParent(blockParent);
const validationSchema = computed(() =>
  yup.object<ProjectModuleDto>({
    title: yup.lazy(() => {
      if (values.block_type === Constants.Block.Type.Hero && values.config?.useProjectTitle) {
        return yup.object().nullable();
      }
      return localizableString(languageEnabled.value);
    }),
    block_type: yup.number().required(),
    config: yup.lazy(
      (value): yup.Schema<any> =>
        blocksStore.getModuleConfigValidationSchema({
          value,
          block_type: values.block_type,
          languageEnabled: languageEnabled.value,
        }),
    ),
  }),
);
const formContext = useForm({ validationSchema });
const { handleSubmit, errors, meta, resetForm, values, setFieldValue, validate } = formContext;
const languageErrors = useLanguageValidationErrors(errors);

// This screen's copy of the block, not the store's, carries the config it is edited with (a page Hero's project
// toggles off): the form loads it, a cancel restores it, and the next save writes it.
const module = useWatchItemLoading<ProjectModuleDto>(moduleId, blocksStore, (item) => {
  item.config = editableBlockConfig(item);
  resetFormToItem(formContext, cloneDeep(item));
});
const moduleTypeLabel = computed(() => {
  const type = module.value?.block_type;
  return type ? blockTypeName(moduleTypes, type) : '';
});

// Watch displayMode changes to trigger re-validation when switching between image/video
watch(
  () => values.config?.displayMode,
  (newMode, oldMode) => {
    if (newMode !== oldMode && (newMode === 'image' || newMode === 'video')) {
      // Re-validate the form when display mode changes
      validate();
    }
  },
);

// Re-validate when Hero toggle changes to update required fields
watch(
  () => values.config?.useProjectTitle,
  (newValue, oldValue) => {
    if (values.block_type === Constants.Block.Type.Hero && newValue !== oldValue) {
      validate();
    }
  },
);

const editLocale = provideEditLocale(languageEnabled);

// COMPUTED
const currentComponent = computed(() => {
  const moduleTypeId = module.value?.block_type;
  if (!moduleTypeId) return null;
  return componentRegistry[moduleTypeId] || null;
});

const isDirty = computed(() => {
  return unref(meta).dirty;
});

const componentProps = computed(() => ({
  languageEnabled: languageEnabled.value,
  editLocale,
  module: module.value,
  values: values,
  themeKey: themeKey.value,
  themeHost: themeHost.value,
}));

const tabs = computed(() => {
  const tabList = [{ id: 'infos', name: t('project-module.tabs.infos'), isDirty: isDirty.value, icon: 'o_info' }];

  if (
    [
      Constants.Block.Type.Hero,
      Constants.Block.Type.Text,
      Constants.Block.Type.Video,
      Constants.Block.Type.Map,
    ].includes(values.block_type)
  ) {
    tabList.push({
      id: 'ctaButtons',
      name: t('project-module.tabs.ctaButtons'),
      isDirty: false,
      icon: 'o_call_to_action',
    });
  }

  if (values.block_type === Constants.Block.Type.Attachments) {
    tabList.push({ id: 'contexts', name: t('project-module.tabs.contexts'), isDirty: false, icon: 'link' });
  }
  if (values.block_type === Constants.Block.Type.Faq) {
    tabList.push({ id: 'faqs', name: t('project-module.tabs.questions'), isDirty: false, icon: 'help_outline' });
  }
  if (values.block_type === Constants.Block.Type.Timeline) {
    tabList.push({ id: 'steps', name: t('project-module.tabs.steps'), isDirty: false, icon: 'format_list_bulleted' });
  }
  if (values.block_type === Constants.Block.Type.ContactList) {
    tabList.push({
      id: 'contactListMembers',
      name: t('project-module.tabs.contactListMembers'),
      isDirty: false,
      icon: 'o_contact_mail',
    });
  }
  if (values.block_type === Constants.Block.Type.Event) {
    tabList.push({
      id: 'events',
      name: t('project-module.tabs.events'),
      isDirty: false,
      icon: 'event',
    });
  }
  if (values.block_type === Constants.Block.Type.Feedback) {
    tabList.push({
      id: 'feedback',
      name: t('project-module.tabs.feedback'),
      isDirty: false,
      icon: 'o_chat',
    });
  }
  if (values.block_type === Constants.Block.Type.QuestionAnswer) {
    tabList.push({
      id: 'questionAnswers',
      name: t('project-module.tabs.questionAnswers'),
      isDirty: false,
      icon: 'help_outline',
    });
  }
  if (
    values.block_type === Constants.Block.Type.DocumentSubmission &&
    configurationStore.isFeatureEnabled('documentSubmissions')
  ) {
    tabList.push({
      id: 'documentSubmissions',
      name: t('project-module.tabs.documentSubmissions'),
      isDirty: false,
      icon: 'o_upload_file',
    });
  }
  if (values.block_type === Constants.Block.Type.News) {
    tabList.push({
      id: 'news',
      name: t('project-module.tabs.news'),
      isDirty: false,
      icon: 'newspaper',
    });
  }
  return tabList;
});

// The web draws a Hero on a page with its URL buttons only (`getPageHeroCtaButtons`), so it offers no link to a block.
const allowModuleLinks = computed(
  () => !(values.block_type === Constants.Block.Type.Hero && props.parentType === Constants.Block.ParentType.Page),
);

const { moduleOptions } = useModuleBreadcrumbOptions(blockParent, blockRouteFor);
// The blocks a CTA button may scroll to: those of this block's parent, which the breadcrumb options fetch.
const parentBlocks = computed(() => blocksStore.getBlocksByParent(blockParent.value));

const onSubmit = handleSubmit(async (values) => {
  console.debug('submitting', values);
  wrapSavingOperation(
    async () => await blocksStore.saveModule(values as ProjectModuleDto),
    () => resetForm({ values: module.value! }),
  );
});

const onCancel = () => {
  if (module.value) {
    resetForm({ values: module.value });
  } else {
    console.warn('onCancel: module is null');
  }
};

// ROUTER
onBeforeRouteLeave(formSaveNavigationGuard(meta));
onBeforeRouteUpdate((to, from, next) => {
  if (!hasRouteChanged(to, from)) {
    return formSaveNavigationGuard(meta)(to, from, next);
  }
  next();
});

const breadcrumbs = computed(() => {
  const breadcrumbItems: BreadcrumbElement[] = [
    {
      label: parentTitle.value,
      to: parentLocation.value,
      icon: props.parentIcon,
    },
    {
      // The parent's blocks, named as the parent's screen names them; the dropdown lists them by title.
      label: t(blockLabels.value.blocksKey),
      to: blockRouteFor(moduleId.value),
      icon: blockLabels.value.icon,
      options: moduleOptions.value,
      activeId: module.value?.id,
    },
  ];
  return breadcrumbItems;
});

const preview = () => {
  const previewUrl = blocksStore.previewURL(moduleId.value);
  if (previewUrl) {
    window.open(previewUrl, '_blank')?.focus();
  }
};

function resetTitle() {
  const emptyTitle = {
    en: '',
    fr: '',
  };
  setFieldValue('title', emptyTitle);
}

function setTitle(consultation: ProjectConsultationListItemDto) {
  setFieldValue('config.consultationId', consultation.id);
  setFieldValue('title', consultation.title);
}

function handleUpdate(tabId: string) {
  openedTab.value = tabId;
}

// Lifecycle - Load module
watch(
  moduleId,
  async (newModuleId) => {
    if (Number.isNaN(newModuleId) || newModuleId < 0) {
      return;
    }

    const tabId = window.location.hash ? window.location.hash.substring(1) : 'infos';
    if (openedTab.value !== tabId) {
      openedTab.value = tabId;
      dynamicTabsRef.value?.setActiveTab(tabId);
    }

    await blocksStore.fetchModuleById(newModuleId, blockParent.value);
  },
  { immediate: true },
);

const moduleTitle = computed(() =>
  getModuleDisplayTitleWithFallback(module.value, project.value, languageEnabled.value, moduleTypeLabel.value),
);
</script>

<template>
  <q-page class="module-detail">
    <top-page-navigation
      :breadcrumbs="breadcrumbs"
      :language-enabled="languageEnabled"
      :to="parentLocation"
      @preview="preview"
    />
    <div class="module-detail__title">
      <div class="module-detail__title-left">
        <q-icon class="module-detail__title-icon" :name="blockLabels.icon" size="2.35rem" />

        <div class="module-detail__title-text">
          <h1>{{ moduleTitle }}</h1>
          <q-chip class="module-detail__type-pill" color="primary" text-color="white">{{ moduleTypeLabel }}</q-chip>
        </div>
      </div>
    </div>
    <div v-if="module">
      <dynamic-tabs ref="dynamicTabsRef" :tabs="tabs" :active="openedTab" keep-alive @update="handleUpdate">
        <template #infos>
          <form-actions
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            v-model:editLocale="editLocale"
            :languageEnabled="languageEnabled"
            :show-language-switcher="values.block_type != Constants.Block.Type.Consultation"
            v-if="values.block_type != Constants.Block.Type.Consultation || meta.dirty"
          />
          <component
            :is="currentComponent"
            v-bind="componentProps"
            @save-image="imageHandlers.save"
            @delete-image="imageHandlers.clear"
            @reset-consultation="resetTitle"
            @set-consultation="setTitle"
          />
        </template>
        <template #contexts>
          <context-list
            :parent="Constants.Context.Parent.Module"
            :parent-id="moduleId"
            :languageEnabled="languageEnabled"
          />
        </template>
        <template #faqs>
          <faq-list :module="module" :language-enabled="languageEnabled" />
        </template>
        <template #steps>
          <timeline-step-list :module="module" :language-enabled="languageEnabled" />
        </template>
        <template #contactListMembers>
          <contact-list-member-list :module="module" :language-enabled="languageEnabled" />
        </template>
        <template #feedback>
          <feedback-entry-list :module="module" :language-enabled="languageEnabled" />
        </template>
        <template #questionAnswers>
          <question-answer-list :module="module" :language-enabled="languageEnabled" />
        </template>
        <template #documentSubmissions>
          <document-submission-list :module="module" :language-enabled="languageEnabled" />
        </template>
        <template #ctaButtons>
          <form-actions
            :onCancel="onCancel"
            :onSubmit="onSubmit"
            :languageErrors="languageErrors"
            :meta="meta"
            v-model:editLocale="editLocale"
            :languageEnabled="languageEnabled"
            :show-language-switcher="values.block_type != Constants.Block.Type.Consultation"
            v-if="values.block_type != Constants.Block.Type.Consultation || meta.dirty"
          />
          <tab-cta-buttons
            :language-enabled="languageEnabled"
            :blocks="parentBlocks"
            :block-id="moduleId"
            :allow-module-links="allowModuleLinks"
          />
        </template>
        <template #events>
          <event-list :module="module" :language-enabled="languageEnabled" />
        </template>
        <template #news>
          <news-list :module="module" :language-enabled="languageEnabled" />
        </template>
      </dynamic-tabs>
    </div>
  </q-page>
</template>
<style lang="scss">
.module-detail {
  padding: 16px;

  &__title {
    margin-top: 20px;
    padding: 0 10px;
    display: flex;
    justify-content: space-between;
  }

  &__title-left {
    display: flex;
    align-items: flex-start;
    gap: 8px;
    max-width: 80%;
  }

  &__title-text {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    line-height: 1.1;
  }

  &__title-text h1 {
    margin: 0;
    max-width: 100%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__title-icon {
    line-height: 1;
    margin-top: 0.1rem;
  }

  &__type-pill {
    display: inline-flex;
  }
}

.information-general {
  padding: 10px;

  &__header {
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding: 10px 0;

    .title {
      color: $color-primary;
      font-size: $small-title;
    }
  }
}
</style>
